import { IMessage } from '@stomp/stompjs';
import aiShine from 'assets/images/png/aiShine.png';
import dbLogoV2 from 'assets/images/png/dbLogoV2.png';
import { ReactComponent as IconSend } from 'assets/images/svg/graySendIcon.svg';
import React, { useState, useEffect, useRef } from 'react';

import './Chat.scss';
import AgentExecution from './agentExecution/AgentExecution';
import Message from './message/Message';
import MutationMessage from './message/MutationMessage';
import { ChatMessage, useMessagesContext } from './MessagesContext';
import QueryExplorerMessage from './queryExplorerMessage/QueryExplorerMessage';
import stompService from './socket/stompService';

import Insights from 'components/insights/Insights';

const Chat = (): React.ReactElement => {
  const { messages, addMessage, agentExecuting } = useMessagesContext();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null); // Ref for tracking the chat end
  const scrollToBottom = () => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom(); // Automatically scroll to the bottom when messages change
  }, [messages]);

  const onMessageReceived = (message: IMessage) => {
    try {
      // eslint-disable-next-line no-console
      console.log('received socket message:', message.body);
      const body = JSON.parse(message.body);
      if (
        body.type === 'NEW_CAMPAIGN' ||
        body.type === 'NEW_EXPORT' ||
        body.type === 'NEW_ACCOUNT_LIST'
      ) {
        // Create a ChatMessage object with the message body directly passed into the content
        const chatMessage: ChatMessage = {
          id: Date.now(),
          content: body, // Store the entire JSON object in the message content
          isUser: false,
          mutationMessage: true,
        };

        setTimeout(() => {
          // We get the socket message too quick. this slows it down so it feels more natural.
          addMessage(chatMessage);
        }, 2000);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing message body:', error);
    }
  };

  // Socket listening code
  useEffect(() => {
    // Activate the STOMP client
    stompService.activate();

    // Subscribe to the topic where the server sends messages
    const subscription = stompService.subscribe('/topic/reply', onMessageReceived);

    // Cleanup on unmount
    return () => {
      subscription.unsubscribe();
      stompService.deactivate();
    };
  }, []);

  const handleSendMessage = () => {
    if (input.trim()) {
      const userInput = input.trim();
      setInput(''); // Clear the input field

      // Add user's message
      addMessage({
        id: Date.now(),
        text: userInput,
        isUser: true,
      });

      // Add AgentExecution to handle agent's response
      addMessage({
        id: Date.now() + 1,
        isUser: false,
        agentExecution: true,
        userInput,
      });
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (agentExecuting) return;
    if (e.key === 'Enter') handleSendMessage();
  };

  const renderMessage = (message: ChatMessage, index: number) => (
    <div
      key={message.id + index}
      className={`${message.isUser ? 'chat__message--user' : 'chat__message--bot'}`}
    >
      {!message.isUser && <img src={dbLogoV2} className='db-logo' alt='DB Logo' loading='lazy' />}
      {message.type === 'queryExplorer' ? (
        <QueryExplorerMessage key={message.id + index} />
      ) : message.agentExecution ? (
        <AgentExecution
          key={message.id + index}
          userInput={message.userInput ?? ''}
          isUser={false}
          onContentUpdate={scrollToBottom} // Pass the scrollToBottom function here
        />
      ) : message.mutationMessage ? (
        <MutationMessage
          key={message.id + index}
          content={message.content}
          sender={message.isUser}
        />
      ) : (
        <Message
          key={message.id + index}
          text={message.text || ''}
          isUser={message.isUser}
          choices={message.choices}
          onChoiceSelect={message.onChoiceSelect}
        />
      )}
    </div>
  );

  return (
    <div className={'chat-container'}>
      {/* According to figma */}
      {/* <header className='chat__header'>
        <div className='chat__title'>
          <img src={dbLogoV2} className='chat-db-logo' alt='Demandbase Logo' loading='lazy' />
          <div>
            <h2>Demandbase AI</h2>
            <h3>Insights & Recommendations</h3>
          </div>
        </div>
      </header> */}

      <div className={`chat ${messages.length === 0 ? 'chat--centered' : ''}`}>
        <div></div> {/* Empty div for alignment purpose remove later and fix */}
        {messages.length === 0 && (
          <span className='chat__hero'>
            <h2 className='chat__hero--title'>What can I help with?</h2>
            <p className='chat__hero--description'>
              Ask me anything about Demandbase, like how to create a new journey, export data, or
              create a new account list.
            </p>
          </span>
        )}
        {/* If there are messages, show Insights above the chat input */}
        <div className={`chat__messages ${messages.length === 0 ? 'chat__messages--empty' : ''}`}>
          {messages.map((message, index) => {
            return renderMessage(message, index);
          })}
          {/* The ref to scroll to */}
          <div ref={messagesEndRef} />
        </div>
        <div className='chat__input'>
          <img src={aiShine} alt='AI Shine Icon' className='input-icon' loading='lazy' />
          <input
            type='text'
            placeholder='Ask Demandbase bot a question...'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
          />
          <button onClick={handleSendMessage} disabled={agentExecuting}>
            <IconSend />
          </button>
        </div>
        {/* If there are no messages, show Insights below the chat input */}
        {/* {messages.length === 0 && <Insights />} */}
      </div>
    </div>
  );
};

export default Chat;
